import { Row, Table, flexRender } from '@tanstack/react-table';
import { StyledIconSmall, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { StyledTableData, StyledTableDataDiv, StyledTableRow } from '../../../../shared/styles/components/table.styles';
import { useCallback, useEffect, useState } from 'react';

import APIService from '../../../../services/api.service';
import { AddButton } from '../add-edit/AddButton';
import { ExpandRowIcon } from '../expanded-row/ExpandRowIcon';
import { Item } from '../../types/item.types';
import { Query } from '../../../../shared/types/query.types';
import SSRIcon from '@ingka/ssr-icon';
import { StatusIndicator } from '../../../status/components/StatusIndicator';
import baseIcon from '@ingka/ssr-icon/paths/arrow-right';
import basedOnIcon from '@ingka/ssr-icon/paths/arrow-left';
import hideIcon from '@ingka/ssr-icon/paths/visibility-hide';
import infoIcon from '@ingka/ssr-icon/paths/information-circle';
import { rgba } from '../../../../shared/styles/core/utils';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useAuth } from '../../../auth/state/auth.state';
import { useBomVersionState } from '../../../bom/state/bom-version.state';
import { useExpandedOnRow } from '../../state/expanded-rows.state';
import { useHover } from '@use-gesture/react';
import { useHoverState } from '../../state/hover-and-scroll.state';
import { useMutation } from 'react-query';
import { useTableDataState } from '../../state/table-data.state';

const StyledEndOfRowButtonDiv = styled.div`
   position: absolute;
   right: ${tokens.space.xs};
   border: none !important;
   padding-right: ${tokens.space.md};
`;

const StyledGroupedTableRow = styled(StyledTableRow) <{
   boldFont?: boolean;
   isVersioned?: boolean;
   hasVersion?: boolean;
}>`
   ${props => props.boldFont && `font-weight: ${tokens.font.weight.bold};`}
   
   ${props => (props.hasVersion || props.isVersioned) && `
      border-left: 4px solid ${theme.colors.state.version} !important;
      background-color: ${props.isVersioned
         ? rgba(theme.colors.state.version, 0.08)
         : rgba(theme.colors.state.version, 0.03)};
   `}
   
   ${props => props.isVersioned && `
      td:nth-of-type(1) {
         padding-left: ${tokens.space.xl};
      }
      td:nth-of-type(2) {
         padding-left: ${tokens.space.md};  
      }
   `}
`;

type TableRowProps = {
   row: Row<Item>;
   index: number;
   onItemsUpdate: (items: Item[]) => void;
   instance: Table<Item>;
};

type PiaImportStatusButtonProps = {
   status: string;
};

export const TableRow = ({ row, index, onItemsUpdate, instance }: TableRowProps) => {
   const { isExpanded, toggleExpanded } = useExpandedOnRow(index);
   const [hovering, setHovering] = useState(false);
   const isInactiveHidden = useTableDataState((state) => state.query.inactivatedRowsHidden || false);
   const rows = instance?.getRowModel().rows || [];
   const { resetImportStatus } = useImportStatusReset(rows.map((row) => row.original), onItemsUpdate);
   const bind = useHover(({ hovering }) => {
      useHoverState.setState({ hoverIndex: hovering ? index : -1 });
      setHovering(!!hovering);
   });
   const isVersionedItem = row.original.isVersionedItem || false;
   const bomVersionState = useBomVersionState.getState();
   const hasVersion = !isVersionedItem && !!bomVersionState.getItemBomVersion(row.original.itemNo);

   const setQuery = useCallback((query: Query) => {
      useTableDataState.setState({ query });
   }, []);

   const handleBaseItemClick = useCallback(async (e: React.MouseEvent, itemNo: string) => {
      e.stopPropagation();
      e.preventDefault();

      try {
         const relatedItemNos = await APIService.getBaseAndBasedOn(itemNo);

         if (relatedItemNos && relatedItemNos.length > 0) {
            const currentQuery = useTableDataState.getState().query;

            const newQuery: Query = {
               pageSize: currentQuery.pageSize,
               offset: currentQuery.offset,
               progress: currentQuery.progress,
               itemNos: relatedItemNos,
               inactivatedRowsHidden: currentQuery.inactivatedRowsHidden
            };

            setQuery(newQuery);
         }
      } catch (error) {
         console.error("Error fetching base and related items:", error);
      }
   }, [setQuery]);

   // Show hide icon on top row level based on if inactive material is hidden
   const hideIconIsApplicable = () => {
      let result = false;
      row.original.subItems.forEach((subItem) => {
         if (subItem.active === false) {
            result = true;
         }
      });
      return result && isInactiveHidden;
   };

   // Counter of hidden material rows
   const hiddenMaterialRowCount = () => {
      let result = 0;
      row.original.subItems.forEach((subItem) => {
         if (subItem.active === false) {
            result += 1;
         }
      });
      return result;
   };

   // only re-render on hover state change if the change applies to this row
   const hoverIndex = useHoverState(
      (state) => state.hoverIndex,
      (newIndex, oldIndex) => {
         if (newIndex !== index && oldIndex !== index) return true;
         return newIndex === oldIndex;
      }
   );
   const { isAdmin, isEditor } = useAuth();

   const memoizedAction = useCallback(() => {
      toggleExpanded(index);
      if (!isExpanded) {
         resetImportStatus(row);
      }
   }, [toggleExpanded, index, isExpanded, resetImportStatus, row]);

   const { onClick, setMouseDown, setMouseUp } = useSingleAndDoubleClick(memoizedAction);

   return (
      <StyledGroupedTableRow
         isVersioned={isVersionedItem}
         hasVersion={hasVersion}
         key={index}
         onMouseDown={(e) => setMouseDown(e.clientX)}
         onMouseUp={(e) => setMouseUp(e.clientX)}
         onClick={onClick}
         boldFont={isExpanded}
         {...(bind() as object)}
      >
         {row.getVisibleCells().map((cell, cellIndex) => {
            const secondColumn = row.getVisibleCells().indexOf(cell) === 1;
            const { parentItemNo } = row.original;
            const basedIcon = row.original.parentItemNo === null ? baseIcon : basedOnIcon;
            const iconColor = row.original.parentItemNo === null ? theme.colors.text.primary : theme.colors.bg.tertiary;

            return (
               <StyledTableData
                  key={cell.id}
                  style={{ textAlign: cell.column.columnDef.meta?.rightAligned ? 'right' : 'left' }}>
                  <StyledTableDataDiv
                     key={cell.id}
                     style={{
                        justifyContent: cell.column.columnDef.meta?.rightAligned ? 'flex-end' : 'flex-start',
                        width: '100%'
                     }}>
                     {cellIndex === 0 && <ExpandRowIcon isExpanded={isExpanded} />}
                     {cell.column.id !== "progress" &&
                        <div style={cell.row.original.deleted && cell.column.id === 'itemNo' ? { color: theme.colors.text.disabled } : undefined}>
                           {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                     }
                     {row && secondColumn && !row.original.isLoose &&
                        <StyledTooltip title={parentItemNo ? `Based on ${parentItemNo}` : ''}>
                           <span
                              onClick={(e) => handleBaseItemClick(e, parentItemNo || row.original.itemNo)}
                              style={{ cursor: 'pointer' }}
                           >
                              <StyledIconSmall paths={basedIcon} color={iconColor} />
                           </span>
                        </StyledTooltip>
                     }
                     {row && hideIconIsApplicable() && secondColumn && <StyledIconSmall paths={hideIcon} color={theme.colors.text.secondary} />}
                     {row && hideIconIsApplicable() && secondColumn && hiddenMaterialRowCount()}
                     {cellIndex === row.getVisibleCells().length - 1 &&
                        !row.original.deleted &&
                        hovering &&
                        hoverIndex === index &&
                        (isEditor || isAdmin ? (
                           <>
                              <StyledEndOfRowButtonDiv>
                                 <AddButton parentItem={row.original!} />
                              </StyledEndOfRowButtonDiv>
                           </>
                        ) : (
                           <></>
                        ))}
                     {cell.column.id === "progress" && <StatusIndicator status={row.original.progress} />}
                     {cell.column.id === "piaImportStatus" && <PiaImportStatusIcon status={row.original.piaImportStatus} />}
                  </StyledTableDataDiv>
               </StyledTableData>
            );
         })}
      </StyledGroupedTableRow>
   );
};

const PiaImportStatusIcon = (p: PiaImportStatusButtonProps) => {
   if (p.status === "") {
      return <></>
   }

   return <><SSRIcon style={{ paddingLeft: tokens.space.sm }} paths={infoIcon} /></>
}

const useImportStatusReset = (originalItems: Item[], updateItemsCb: (items: Item[]) => void) => {
   const { mutate } = useMutation(APIService.acknowledgePiaItemUpdates, {
      onSuccess: (_, vars) => {
         // Do not update the table, read more here: https://inter-ikea.atlassian.net/browse/MMA-58
         // queryClient.invalidateQueries(['data', query]);
         // Mutate it manually instead:
         const itemNo = [vars].flat()[0].itemNo;

         if (itemNo) {
            const items = [...originalItems];
            const index = items.findIndex((i) => i.itemNo === itemNo)

            items[index] = {
               ...items[index],
               updatedByPia: false,
               newFromPia: false,
               piaImportStatus: ''
            }

            if (originalItems[index].piaImportStatus !== items[index].piaImportStatus) {
               updateItemsCb(items)
            }
         }
      },
      onError: (error: any) => console.log(error)
   });
   function resetImportStatus(item: Row<Item>) {
      mutate(item.original);
   }
   return { resetImportStatus };
};

const useSingleAndDoubleClick = (action: () => void, delay = 250) => {
   const [click, setClick] = useState(0);
   const [mouseDown, setMouseDown] = useState(0);
   const [mouseUp, setMouseUp] = useState(0);

   useEffect(() => {
      const timer = setTimeout(() => {
         if (click === 1) {
            if (mouseUp < mouseDown + 5 && mouseUp > mouseDown - 5) {
               action();
            }
         }
         setClick(0);
      }, delay);

      return () => clearTimeout(timer);
   }, [click, action, delay, mouseDown, mouseUp]);

   return {
      onClick: () => setClick((prev) => prev + 1),
      setMouseUp,
      setMouseDown
   };
};
